import React, { FC } from "react";
import { DownloadMenuList, DownloadMenuListProps } from "./DownloadMenuList";
import { MenuPopover, MenuTriggerVariant } from "./MenuPopover";
import { GlyphDownload } from "./glyphs";

/**
 * The one download control in the app: trigger + grouped panel + placement.
 *
 * There used to be TWO near-identical implementations — `DownloadDropdown` and
 * the video page's `DownloadBtn` — with the same flat list and the same four
 * defects (a `bg-gray-400` that compiles to nothing and so renders an invisible
 * button, a hardcoded white label, a hardcoded grey glyph, an opaque shadow).
 * Fixing one left the other untouched, so the same button looked identical
 * closed and behaved differently open. Both are now thin adapters over this.
 */

export type DownloadTriggerVariant = MenuTriggerVariant;

interface Props extends Omit<DownloadMenuListProps, "onPick"> {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  onPick: DownloadMenuListProps["onPick"];
  variant?: DownloadTriggerVariant;
  /** Greys the trigger and refuses to open it. */
  disabled?: boolean;
  /** Primary variant only — "Downloading..." while a zip is being prepared. */
  label?: string;
  /** Wraps the icon triggers in an MUI Tooltip, as `DownloadDropdown` always did. */
  tooltip?: string;
  /** Stops the click reaching a clickable card behind the button. */
  stopPropagation?: boolean;
}

export const DownloadPopover: FC<Props> = ({
  open,
  onToggle,
  onClose,
  onPick,
  variant = "primary",
  disabled = false,
  label = "Download",
  tooltip,
  stopPropagation = true,
  ...listProps
}) => (
  <MenuPopover
    open={open}
    onToggle={onToggle}
    onClose={onClose}
    variant={variant}
    disabled={disabled}
    label={label}
    tooltip={tooltip}
    triggerTitle="Download"
    disabledTitle="Not available for download"
    glyph={(props) => <GlyphDownload {...props} />}
    stopPropagation={stopPropagation}
  >
    <DownloadMenuList {...listProps} onPick={onPick} />
  </MenuPopover>
);
